import { useEffect, useMemo, useState } from "react";
import { Plus, Trash, ChartLineUp, Scales, Percent, Camera, X, Calculator, Share } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import WinCardShare from "./WinCardShare";

const KG_TO_LB = 2.20462;
const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const SITES = [
  { key: "triceps", label: "Triceps" },
  { key: "biceps", label: "Biceps" },
  { key: "chest", label: "Chest" },
  { key: "abdominal", label: "Belly button (abdominal)" },
  { key: "subscapular", label: "Shoulder blades (subscapular)" },
  { key: "suprailiac", label: "Hips (suprailiac)" },
  { key: "thigh", label: "Quads (thigh)" },
  { key: "calf", label: "Calves" },
];

export default function MetricsPanel({ clientId, clientName, canEdit, unit = "kg", mode = "trainer" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [photoModal, setPhotoModal] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [showWinCard, setShowWinCard] = useState(false);
  const brand = useBrand();

  const load = async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        api.get(`/clients/${clientId}/metrics`),
        api.get(`/clients/${clientId}/metrics/timeline`),
      ]);
      setRows(m.data);
      setTimeline(t.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (clientId) load(); /* eslint-disable-next-line */ }, [clientId]);

  // Best body-fat for KPI: use calculated if available, fall back to manual entry
  const effectiveBf = (r) => r?.body_fat_calculated ?? r?.body_fat_percent ?? null;

  const sorted = useMemo(() => [...rows].sort((a, b) => a.recorded_at.localeCompare(b.recorded_at)), [rows]);
  const latest = sorted[sorted.length - 1];
  const previous = sorted[sorted.length - 2];
  const weightDelta = latest?.weight_kg != null && previous?.weight_kg != null
    ? latest.weight_kg - previous.weight_kg : null;
  const latestBf = effectiveBf(latest);
  const prevBf = effectiveBf(previous);
  const bfDelta = latestBf != null && prevBf != null ? latestBf - prevBf : null;

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/metrics/${id}`);
      toast.success("Entry deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="metrics-panel">
      {/* KPI cards */}
      <div className="grid sm:grid-cols-2 gap-4">
        <KpiCard
          label="Current weight"
          icon={<Scales size={18} weight="duotone" />}
          value={latest?.weight_kg != null
            ? (unit === "lb" ? `${(latest.weight_kg * KG_TO_LB).toFixed(1)} lb` : `${latest.weight_kg.toFixed(1)} kg`)
            : "—"}
          delta={weightDelta != null
            ? (unit === "lb" ? `${weightDelta >= 0 ? "+" : ""}${(weightDelta * KG_TO_LB).toFixed(1)} lb` : `${weightDelta >= 0 ? "+" : ""}${weightDelta.toFixed(1)} kg`)
            : null}
          deltaGood={weightDelta != null ? weightDelta <= 0 : null}
          dataTestId="kpi-weight"
        />
        <KpiCard
          label="Body fat"
          icon={<Percent size={18} weight="duotone" />}
          value={latestBf != null ? `${latestBf.toFixed(1)}%` : "—"}
          delta={bfDelta != null ? `${bfDelta >= 0 ? "+" : ""}${bfDelta.toFixed(1)} pp` : null}
          deltaGood={bfDelta != null ? bfDelta <= 0 : null}
          hint={latest?.body_fat_calculated != null ? "From skinfolds (J-P 3-site)" : (latest?.body_fat_percent != null ? "Manually entered" : null)}
          dataTestId="kpi-bf"
        />
      </div>

      {/* Since-starting narrative */}
      {timeline && timeline.entry_count >= 2 && (timeline.weight || timeline.body_fat) && (
        <div className="card-flat p-5 border-l-2 border-brand" data-testid="metrics-since-starting">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="tracker text-[10px] text-brand">[ SINCE STARTING ]</div>
            <div className="flex items-center gap-3">
              <div className="tracker text-[10px] text-zinc-500">
                {timeline.duration_days > 0 ? `${timeline.duration_days} day${timeline.duration_days === 1 ? "" : "s"} tracked` : "Tracking started"} · {timeline.entry_count} entries
              </div>
              {((timeline.weight && timeline.weight.points >= 2) || (timeline.body_fat && timeline.body_fat.points >= 2)) && (
                <button
                  type="button"
                  onClick={() => setShowWinCard(true)}
                  className="inline-flex items-center gap-1.5 tracker text-[10px] text-emerald-400 hover:text-emerald-300 transition-colors"
                  data-testid="metrics-share-win-card"
                >
                  <Share size={12} weight="bold" /> SHARE
                </button>
              )}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 mt-4">
            {timeline.weight && timeline.weight.points >= 2 && (() => {
              const w = timeline.weight;
              const display = (val) => unit === "lb" ? `${(val * KG_TO_LB).toFixed(1)} lb` : `${val.toFixed(1)} kg`;
              const changeRaw = w.change ?? 0;
              const change = unit === "lb" ? changeRaw * KG_TO_LB : changeRaw;
              const sign = change > 0 ? "+" : "";
              const good = changeRaw <= 0;
              return (
                <div data-testid="since-starting-weight">
                  <div className="text-zinc-500 text-xs flex items-center gap-1.5"><Scales size={12} weight="duotone" /> Weight</div>
                  <div className="font-display text-3xl mt-1">{display(w.current)}</div>
                  <div className="text-zinc-500 text-xs mt-1">
                    from <span className="text-zinc-300">{display(w.baseline)}</span>
                  </div>
                  <div className={`tracker text-xs mt-2 ${good ? "text-emerald-400" : "text-amber-400"}`}>
                    {sign}{change.toFixed(1)} {unit === "lb" ? "lb" : "kg"} ({w.change_pct != null ? `${sign}${w.change_pct.toFixed(1)}%` : "—"})
                  </div>
                </div>
              );
            })()}
            {timeline.body_fat && timeline.body_fat.points >= 2 && (() => {
              const bf = timeline.body_fat;
              const change = bf.change ?? 0;
              const sign = change > 0 ? "+" : "";
              const good = change <= 0;
              return (
                <div data-testid="since-starting-bf">
                  <div className="text-zinc-500 text-xs flex items-center gap-1.5"><Percent size={12} weight="duotone" /> Body fat</div>
                  <div className="font-display text-3xl mt-1">{bf.current.toFixed(1)}%</div>
                  <div className="text-zinc-500 text-xs mt-1">
                    from <span className="text-zinc-300">{bf.baseline.toFixed(1)}%</span>
                  </div>
                  <div className={`tracker text-xs mt-2 ${good ? "text-emerald-400" : "text-amber-400"}`}>
                    {sign}{change.toFixed(1)} pp
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Sparkline */}
      {sorted.length >= 2 && (
        <div className="card-flat p-5" data-testid="metrics-chart">
          <div className="flex items-center gap-2 tracker text-zinc-500 mb-3">
            <ChartLineUp size={14} weight="bold" /> Trend ({sorted.length} entries)
          </div>
          <Sparkline rows={sorted} unit={unit} effectiveBf={effectiveBf} />
        </div>
      )}

      {/* History */}
      <div className="flex items-center justify-between">
        <div className="tracker text-brand">[ History ]</div>
        {canEdit && (
          <button onClick={() => setShowForm(true)} className="btn-primary !py-2 !px-4 !text-[10px]" data-testid="add-metric-btn">
            <Plus size={12} weight="bold" /> Add entry
          </button>
        )}
      </div>
      <div className="space-y-3">
        {loading && <div className="card-flat p-6 text-center text-zinc-600">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="card-flat p-10 text-center text-zinc-600">
            No measurements yet{canEdit ? " — add the first entry above." : ". Your coach hasn't logged any readings yet."}
          </div>
        )}
        {rows.map((r) => (
          <EntryCard
            key={r.id}
            r={r}
            unit={unit}
            canEdit={canEdit}
            mode={mode}
            onDelete={() => remove(r.id)}
            onPhotoClick={(p) => setPhotoModal({ photo: p, all: r.photos || [] })}
          />
        ))}
      </div>

      {showForm && (
        <AddMetricModal
          clientId={clientId}
          clientName={clientName}
          unit={unit}
          mode={mode}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}
      {photoModal && <PhotoLightbox photo={photoModal.photo} all={photoModal.all} onClose={() => setPhotoModal(null)} />}
      {showWinCard && timeline && (
        <WinCardShare
          timeline={timeline}
          brand={{
            name: brand?.brand_name,
            accent: brand?.brand_accent_color,
            logo: brand?.brand_logo_b64,
          }}
          unit={unit}
          clientName={clientName}
          onClose={() => setShowWinCard(false)}
        />
      )}
    </div>
  );
}

function KpiCard({ label, icon, value, delta, deltaGood, hint, dataTestId }) {
  return (
    <div className="card-flat p-5" data-testid={dataTestId}>
      <div className="flex items-center gap-2 tracker text-zinc-500 mb-2">{icon}{label}</div>
      <div className="font-display text-3xl">{value}</div>
      {delta && <div className={`tracker mt-1 ${deltaGood ? "text-emerald-400" : "text-orange-400"}`}>{delta} since last</div>}
      {hint && <div className="text-[10px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}

function EntryCard({ r, unit, canEdit, mode = "trainer", onDelete, onPhotoClick }) {
  const [open, setOpen] = useState(false);
  const sf = r.skinfolds || {};
  const sfCount = Object.values(sf).filter((v) => v != null).length;
  const photos = r.photos || [];
  const bf = r.body_fat_calculated ?? r.body_fat_percent;
  const clientAuthored = r.author_role === "client";
  // Clients only see delete on entries they themselves logged.
  const showDelete = canEdit && (mode === "trainer" || clientAuthored);
  return (
    <div className="card-flat p-4" data-testid={`metric-row-${r.id}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-6 flex-wrap">
          <div>
            <div className="tracker text-zinc-500 text-[10px]">DATE</div>
            <div className="font-mono text-sm flex items-center gap-2">
              {fmtDate(r.recorded_at)}
              {mode === "trainer" && clientAuthored && (
                <span className="tracker text-[8px] px-1.5 py-0.5 border border-brand/50 bg-brand/10 text-[#A8B9DC]" data-testid={`metric-by-client-${r.id}`}>
                  ADDED BY CLIENT
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="tracker text-zinc-500 text-[10px]">WEIGHT</div>
            <div className="font-display text-xl">{r.weight_kg != null ? (unit === "lb" ? `${(r.weight_kg * KG_TO_LB).toFixed(1)} lb` : `${r.weight_kg.toFixed(1)} kg`) : "—"}</div>
          </div>
          <div>
            <div className="tracker text-zinc-500 text-[10px]">BODY FAT</div>
            <div className="font-display text-xl flex items-center gap-1">
              {bf != null ? `${bf.toFixed(1)}%` : "—"}
              {r.body_fat_calculated != null && <Calculator size={12} weight="bold" className="text-brand-light" title="Calculated from skinfolds" />}
            </div>
          </div>
          {sfCount > 0 && (
            <div>
              <div className="tracker text-zinc-500 text-[10px]">SKINFOLDS</div>
              <div className="font-mono text-sm">{sfCount} site{sfCount === 1 ? "" : "s"}</div>
            </div>
          )}
          {photos.length > 0 && (
            <div className="flex items-center gap-2">
              {photos.slice(0, 4).map((p) => (
                <button
                  key={p.id || p.data_url}
                  onClick={() => onPhotoClick(p)}
                  className="relative w-14 h-14 border border-white/10 overflow-hidden hover:border-brand-light"
                  data-testid={`metric-photo-${r.id}`}
                  title={p.kind}
                >
                  <img src={p.data_url} alt={p.kind} className="w-full h-full object-cover" />
                  <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[8px] tracker text-center py-0.5">{p.kind?.toUpperCase()}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(sfCount > 0 || r.notes) && (
            <button onClick={() => setOpen(!open)} className="btn-ghost !py-1.5 !px-3 !text-[10px]">
              {open ? "Hide" : "Details"}
            </button>
          )}
          {showDelete && (
            <button onClick={onDelete} className="btn-ghost !py-1 !px-2 !text-[10px] text-red-400" data-testid={`del-metric-${r.id}`}>
              <Trash size={11} weight="bold" />
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="mt-4 pt-4 border-t border-white/5 grid sm:grid-cols-2 gap-3 text-sm">
          {SITES.filter((s) => sf[s.key] != null).map((s) => (
            <div key={s.key} className="flex justify-between border-b border-white/5 py-1">
              <span className="text-zinc-400">{s.label}</span>
              <span className="font-mono">{sf[s.key].toFixed(1)} mm</span>
            </div>
          ))}
          {r.body_fat_calculated != null && (
            <div className="flex justify-between border-b border-white/5 py-1 col-span-full">
              <span className="text-brand-light">Calc body fat (Jackson-Pollock 3-site, {r.sex === "M" ? "♂" : "♀"} age {r.age})</span>
              <span className="font-mono text-brand-light">{r.body_fat_calculated.toFixed(1)}%</span>
            </div>
          )}
          {r.notes && <div className="col-span-full text-zinc-400 italic mt-2">{r.notes}</div>}
        </div>
      )}
    </div>
  );
}

function Sparkline({ rows, unit, effectiveBf }) {
  const wData = rows.filter((r) => r.weight_kg != null);
  const bData = rows.filter((r) => effectiveBf(r) != null);
  const hasW = wData.length >= 2;
  const hasB = bData.length >= 2;
  if (!hasW && !hasB) return <div className="text-zinc-600 text-sm">Need 2+ entries with the same metric to draw a trend.</div>;
  const W = 800, H = 140, PAD = 20;
  const allDates = rows.map((r) => new Date(r.recorded_at).getTime());
  const t0 = Math.min(...allDates), tN = Math.max(...allDates);
  const dx = tN === t0 ? 1 : tN - t0;
  const x = (t) => PAD + ((t - t0) / dx) * (W - 2 * PAD);
  const linePath = (data, getter) => {
    const vals = data.map(getter);
    const lo = Math.min(...vals), hi = Math.max(...vals);
    const dy = hi === lo ? 1 : hi - lo;
    const y = (v) => H - PAD - ((v - lo) / dy) * (H - 2 * PAD);
    return data.map((r, i) => `${i === 0 ? "M" : "L"}${x(new Date(r.recorded_at).getTime()).toFixed(1)},${y(getter(r)).toFixed(1)}`).join(" ");
  };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32">
      {hasW && <path d={linePath(wData, (r) => r.weight_kg)} fill="none" stroke="var(--brand)" strokeWidth="2" />}
      {hasB && <path d={linePath(bData, effectiveBf)} fill="none" stroke="#FF8C42" strokeWidth="2" strokeDasharray="4 3" />}
      <text x={PAD} y={14} fill="var(--brand)" fontSize="10" className="tracker">WEIGHT ({unit})</text>
      {hasB && <text x={W - PAD - 80} y={14} fill="#FF8C42" fontSize="10" className="tracker">BODY FAT %</text>}
    </svg>
  );
}

// ---------------- Photo handling ----------------
async function compressImage(file, maxWidth = 1024, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function PhotoLightbox({ photo, all, onClose }) {
  const [active, setActive] = useState(photo);
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-6" onClick={onClose} data-testid="photo-lightbox">
      <button onClick={onClose} className="absolute top-6 right-6 btn-ghost !py-2 !px-3"><X size={16} weight="bold" /></button>
      <div className="max-w-4xl max-h-[80vh] flex items-center justify-center mb-4">
        <img src={active.data_url} alt={active.kind} className="max-w-full max-h-[75vh] object-contain border border-white/10" onClick={(e) => e.stopPropagation()} />
      </div>
      <div className="tracker text-zinc-400 mb-2">{active.kind?.toUpperCase()} {active.caption && `· ${active.caption}`}</div>
      {all.length > 1 && (
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          {all.map((p) => (
            <button key={p.id || p.data_url} onClick={() => setActive(p)} className={`w-16 h-16 border-2 ${active === p ? "border-brand" : "border-white/10"}`}>
              <img src={p.data_url} alt={p.kind} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AddMetricModal({ clientId, clientName, unit, onClose, onCreated, mode = "trainer" }) {
  const isClient = mode === "client";
  const todayIso = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    recorded_at: todayIso,
    weight: "",
    body_fat_percent: "",
    sex: "",
    age: "",
    skinfolds: Object.fromEntries(SITES.map((s) => [s.key, ""])),
    notes: "",
  });
  const [photos, setPhotos] = useState([]); // [{kind, data_url, caption}]
  const [busy, setBusy] = useState(false);

  const setSf = (key, v) => setForm((f) => ({ ...f, skinfolds: { ...f.skinfolds, [key]: v } }));

  // Live preview of calculated body fat
  const previewBf = useMemo(() => {
    const sex = form.sex;
    const age = parseInt(form.age, 10);
    if (!sex || !age) return null;
    const sf = form.skinfolds;
    const needed = sex === "M" ? ["chest", "abdominal", "thigh"] : ["triceps", "suprailiac", "thigh"];
    const vals = needed.map((k) => parseFloat(sf[k]));
    if (vals.some((v) => isNaN(v))) return null;
    const total = vals.reduce((a, b) => a + b, 0);
    let bd;
    if (sex === "M") bd = 1.10938 - 0.0008267 * total + 0.0000016 * (total ** 2) - 0.0002574 * age;
    else bd = 1.0994921 - 0.0009929 * total + 0.0000023 * (total ** 2) - 0.0001392 * age;
    if (bd <= 0) return null;
    return Math.max(0, Math.min(80, (495 / bd) - 450));
  }, [form.sex, form.age, form.skinfolds]);

  const handleFile = async (kind, file) => {
    if (!file) return;
    if (file.size > 8_000_000) {
      toast.error("Image too large — please pick something under 8MB");
      return;
    }
    try {
      const dataUrl = await compressImage(file, 1024, 0.82);
      setPhotos((prev) => [...prev, { kind, data_url: dataUrl, caption: "" }]);
    } catch (_e) {
      toast.error("Could not read image");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const weightNum = form.weight === "" ? null : Number(form.weight);
    const bfNum = form.body_fat_percent === "" ? null : Number(form.body_fat_percent);
    const sfPayload = {};
    for (const s of SITES) {
      const v = form.skinfolds[s.key];
      if (v !== "" && !isNaN(parseFloat(v))) sfPayload[s.key] = parseFloat(v);
    }
    if (weightNum == null && bfNum == null && Object.keys(sfPayload).length === 0 && photos.length === 0) {
      toast.error("Enter at least one metric (weight, body fat, skinfolds, or a photo)");
      return;
    }
    const weight_kg = weightNum == null ? null : (unit === "lb" ? weightNum / KG_TO_LB : weightNum);
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/metrics`, {
        weight_kg,
        body_fat_percent: bfNum,
        sex: form.sex || null,
        age: form.age ? parseInt(form.age, 10) : null,
        skinfolds: Object.keys(sfPayload).length ? sfPayload : null,
        photos: photos.length ? photos : null,
        recorded_at: new Date(form.recorded_at + "T12:00:00").toISOString(),
        notes: form.notes || null,
      });
      toast.success(isClient ? "Entry saved" : `Logged for ${clientName}`);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" data-testid="add-metric-modal">
      <form onSubmit={submit} className="card-flat p-8 w-full max-w-3xl my-10" onClick={(e) => e.stopPropagation()}>
        <div className="tracker text-brand mb-2">[ Log entry ]</div>
        <h2 className="font-display text-3xl mb-6">{isClient ? "Log your measurements" : clientName}</h2>

        <div className={`grid gap-4 mb-6 ${isClient ? "sm:grid-cols-2" : "sm:grid-cols-4"}`}>
          <Field label="Date" testid="metric-date">
            <input type="date" value={form.recorded_at} onChange={(e) => setForm({ ...form, recorded_at: e.target.value })} className="w-full px-4 py-3 border" data-testid="metric-date" />
          </Field>
          <Field label={`Weight (${unit})`} testid="metric-weight">
            <input type="number" step="0.1" min="0" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} placeholder={unit === "lb" ? "172.5" : "78.2"} className="w-full px-4 py-3 border" data-testid="metric-weight" />
          </Field>
          {!isClient && (
            <>
              <Field label="Sex">
                <select value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })} className="w-full px-4 py-3 border bg-[#0A0A0A]" data-testid="metric-sex">
                  <option value="">—</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </Field>
              <Field label="Age">
                <input type="number" min="5" max="120" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} placeholder="30" className="w-full px-4 py-3 border" data-testid="metric-age" />
              </Field>
            </>
          )}
        </div>

        {!isClient && (
          <>
            {/* Skinfolds */}
            <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
              <div className="tracker text-zinc-500">[ Skinfold measurements (mm) ]</div>
              {previewBf != null && (
                <div className="tracker text-brand-light flex items-center gap-1" data-testid="bf-preview">
                  <Calculator size={12} weight="bold" /> Calculated body fat: <span className="font-mono text-base font-bold">{previewBf.toFixed(1)}%</span>
                </div>
              )}
            </div>
            <p className="text-zinc-600 text-xs mb-3">
              Set sex + age + the 3-site sum (Male: chest, abdominal, thigh · Female: triceps, suprailiac, thigh) to auto-calculate. The other 5 sites are saved for reference.
            </p>
            <div className="grid sm:grid-cols-4 gap-3 mb-6">
              {SITES.map((s) => (
                <label key={s.key} className="block">
                  <div className="tracker text-zinc-500 text-[10px] mb-1">{s.label}</div>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={form.skinfolds[s.key]}
                    onChange={(e) => setSf(s.key, e.target.value)}
                    placeholder="mm"
                    className="w-full px-3 py-2 border text-sm"
                    data-testid={`sf-${s.key}`}
                  />
                </label>
              ))}
            </div>
          </>
        )}

        <Field label={isClient ? "Body fat % (optional — if you have a reading)" : "Body fat % (manual override — only fill if you have a different reading)"} testid="metric-bf">
          <input type="number" step="0.1" min="0" max="80" value={form.body_fat_percent} onChange={(e) => setForm({ ...form, body_fat_percent: e.target.value })} placeholder="optional" className="w-full px-4 py-3 border" data-testid="metric-bf" />
        </Field>

        {/* Photos */}
        <div className="my-6">
          <div className="tracker text-zinc-500 mb-3">[ Photos ]</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <PhotoUploader kind="before" label="Before" onFile={(f) => handleFile("before", f)} />
            <PhotoUploader kind="after" label="After" onFile={(f) => handleFile("after", f)} />
            <PhotoUploader kind="progress" label="Progress" onFile={(f) => handleFile("progress", f)} />
          </div>
          {photos.length > 0 && (
            <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="photo-preview-list">
              {photos.map((p, i) => (
                <div key={i} className="relative" data-testid={`photo-preview-${i}`}>
                  <img src={p.data_url} alt={p.kind} className="w-full h-24 object-cover border border-white/10" />
                  <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[9px] tracker text-center py-0.5">{p.kind.toUpperCase()}</span>
                  <button type="button" onClick={() => setPhotos((arr) => arr.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/70 hover:bg-red-500 p-1" aria-label="remove">
                    <X size={10} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Field label="Notes (optional)">
          <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-4 py-3 border" placeholder="Morning, post-cardio, fasted, etc." data-testid="metric-notes" />
        </Field>

        <div className="flex justify-end gap-3 mt-8">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="metric-submit">{busy ? "Saving…" : "Save entry"}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="tracker text-zinc-500 mb-2 text-[10px]">{label}</div>
      {children}
    </label>
  );
}

function PhotoUploader({ kind, label, onFile }) {
  return (
    <label className="border border-dashed border-white/15 hover:border-brand-light cursor-pointer p-4 flex flex-col items-center justify-center gap-1 text-center" data-testid={`upload-${kind}`}>
      <Camera size={20} weight="duotone" className="text-brand-light" />
      <div className="tracker text-[10px]">{label}</div>
      <div className="text-[9px] text-zinc-600">Tap to upload</div>
      <input type="file" accept="image/*" capture="environment" onChange={(e) => onFile(e.target.files?.[0])} className="hidden" />
    </label>
  );
}
